import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function TeacherDashboard() {
  const [sessions, setSessions] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [teacher, setTeacher] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    title: "",
    workshop_id: "",
    workshop_queue: [],
    tier: "starsi_zs",
    age_category: "13-16",
    difficulty: "stredni",
    duration_minutes: 45,
    mode: "individual",
    projector_enabled: true,
    safety_mode: false,
    max_participants: 30,
  });
  const navigate = useNavigate();

  // Workshops filtered by chosen tier
  const tierWorkshops = workshops.filter(
    (w) => !w.tiers?.length || w.tiers.includes(form.tier)
  );

  useEffect(() => {
    const t = localStorage.getItem("dm_teacher");
    if (!t) navigate("/teacher/login");
    else setTeacher(JSON.parse(t));
    load();
  }, []);

  const load = async () => {
    try {
      const [s, w] = await Promise.all([api.get("/sessions"), api.get("/workshops")]);
      setSessions(s.data);
      setWorkshops(w.data);
      if (w.data[0]) setForm((f) => ({ ...f, workshop_id: f.workshop_id || w.data[0].id, title: f.title || `Workshop ${new Date().toLocaleDateString("cs-CZ")}` }));    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("dm_token");
        navigate("/teacher/login");
      }
    }
  };

  const logout = () => {
    localStorage.removeItem("dm_token");
    localStorage.removeItem("dm_teacher");
    navigate("/teacher/login");
  };

  const createSession = async () => {
    try {
      const { data } = await api.post("/sessions", form);
      toast.success(`Session vytvořena: ${data.code}`);
      setShowCreate(false);
      navigate(`/teacher/session/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nepodařilo se vytvořit session");
    }
  };

  const active = sessions.filter((s) => s.status !== "finished");
  const finished = sessions.filter((s) => s.status === "finished");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-slate-900 text-white flex items-center justify-center font-black">D</div>
            <div>
              <div className="font-heading font-black text-lg tracking-tight text-slate-900">digitální most</div>
              <div className="text-xs text-slate-500">Lektorský panel</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/teacher/cms" className="text-sm text-slate-600 hover:text-slate-900 font-bold" data-testid="link-cms">
              Knihovna ↗
            </Link>
            {teacher?.role === "admin" && (
              <Link to="/teacher/admin/lecturers" className="text-sm text-slate-600 hover:text-slate-900 font-bold" data-testid="link-admin">
                Lektoři ↗
              </Link>
            )}
            {teacher?.role === "admin" && (
              <Link to="/teacher/admin/nicknames" className="text-sm text-slate-600 hover:text-slate-900 font-bold" data-testid="link-nicknames">
                Přezdívky ↗
              </Link>
            )}
            <span className="text-sm text-slate-600">{teacher?.email}</span>
            <button onClick={logout} className="text-sm text-slate-500 hover:text-slate-900 font-bold" data-testid="btn-logout">
              Odhlásit
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-black tracking-tighter text-slate-900">Tvoje session</h1>
            <p className="text-slate-500 text-sm">Spusť novou hodinu nebo pokračuj v rozjeté.</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-md px-4 py-2.5 text-sm"
            data-testid="btn-new-session"
          >
            + Nová session
          </button>
        </div>

        {active.length === 0 && !showCreate && (
          <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center">
            <div className="text-4xl mb-2">🎓</div>
            <div className="font-heading font-black text-lg text-slate-900">Zatím žádná session</div>
            <div className="text-slate-500 text-sm mt-1">Klikni na „Nová session" a za 10 vteřin máš kód pro třídu.</div>
          </div>
        )}

        {active.length > 0 && (
          <section>
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Aktivní</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {active.map((s) => (
                <SessionCard key={s.id} s={s} onOpen={() => navigate(`/teacher/session/${s.id}`)} />
              ))}
            </div>
          </section>
        )}

        {finished.length > 0 && (
          <section>
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Ukončené</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {finished.map((s) => (
                <SessionCard key={s.id} s={s} onOpen={() => navigate(`/teacher/session/${s.id}`)} />
              ))}
            </div>
          </section>
        )}
      </main>

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg space-y-4">
            <h2 className="text-xl font-heading font-black tracking-tight text-slate-900">Nová session</h2>
            <Field label="Název">
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                data-testid="new-session-title"
              />
            </Field>
            <Field label="Stupeň třídy / věková skupina">
              <select
                value={form.tier}
                onChange={(e) => {
                  const tier = e.target.value;
                  // Reset workshop choice if current isn't allowed for new tier
                  const matching = workshops.filter((w) => !w.tiers?.length || w.tiers.includes(tier));
                  const wid = matching.find((w) => w.id === form.workshop_id) ? form.workshop_id : matching[0]?.id || "";
                  setForm({ ...form, tier, workshop_id: wid });
                }}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                data-testid="new-session-tier"
              >
                <option value="mladsi_zs">3.–5. ZŠ (8–11)</option>
                <option value="starsi_zs">6.–9. ZŠ (11–15)</option>
                <option value="stredni">SŠ (15–19)</option>
              </select>
            </Field>
            <Field label="Workshop">
              <div className="flex gap-2">
                <select
                  value={form.workshop_id}
                  onChange={(e) => setForm({ ...form, workshop_id: e.target.value })}
                  className="flex-1 border border-slate-200 rounded-md px-3 py-2 text-sm"
                  data-testid="new-session-workshop"
                >
                  {tierWorkshops.length === 0 && (
                    <option value="">Pro tento stupeň zatím není žádný workshop</option>
                  )}
                  {tierWorkshops.map((w) => (
                    <option key={w.id} value={w.id}>{w.title}</option>
                  ))}
                </select>
                {form.workshop_id && (
                  <Link
                    to={`/teacher/workshop-preview/${form.workshop_id}`}
                    target="_blank"
                    className="bg-amber-100 hover:bg-amber-200 text-amber-900 font-bold rounded-md px-3 py-2 text-sm border border-amber-300 whitespace-nowrap"
                    data-testid="btn-workshop-preview-link"
                    title="Otevře přípravu pro lektora v novém tabu"
                  >
                    📚 Příprava
                  </Link>
                )}
              </div>
            </Field>

            <Field label="Playlist – další workshopy (volitelné)">
              <div className="space-y-2">
                <div className="text-xs text-slate-500">
                  Žáci se připojí jen jednou. Po skončení prvního workshopu spustíš další z této fronty bez nutnosti znovu kódovat. Hodí se na celé dopoledne.
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.workshop_queue.map((wid, idx) => {
                    const w = workshops.find((x) => x.id === wid);
                    return (
                      <span key={wid + idx} className="inline-flex items-center gap-1 bg-blue-100 text-blue-900 rounded-full pl-3 pr-1 py-1 text-xs font-bold">
                        {idx + 2}. {w?.title || wid}
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, workshop_queue: form.workshop_queue.filter((_, i) => i !== idx) })}
                          className="ml-1 hover:bg-blue-200 rounded-full w-5 h-5 inline-flex items-center justify-center"
                          aria-label="Odebrat"
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
                <select
                  value=""
                  onChange={(e) => {
                    if (!e.target.value) return;
                    setForm({ ...form, workshop_queue: [...form.workshop_queue, e.target.value] });
                  }}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                  data-testid="new-session-add-to-queue"
                >
                  <option value="">+ Přidat další workshop do fronty…</option>
                  {tierWorkshops
                    .filter((w) => w.id !== form.workshop_id && !form.workshop_queue.includes(w.id))
                    .map((w) => (
                      <option key={w.id} value={w.id}>{w.title}</option>
                    ))}
                </select>
              </div>
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Věková kategorie">
                <select value={form.age_category} onChange={(e) => setForm({ ...form, age_category: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                  <option value="10-12">10–12</option>
                  <option value="13-16">13–16</option>
                  <option value="15-18">15–18</option>
                </select>
              </Field>
              <Field label="Obtížnost">
                <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                  <option value="snadna">Snadná</option>
                  <option value="stredni">Střední</option>
                  <option value="tezka">Těžká</option>
                </select>
              </Field>
              <Field label="Délka (min)">
                <input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </Field>
              <Field label="Max účastníků">
                <input type="number" value={form.max_participants} onChange={(e) => setForm({ ...form, max_participants: parseInt(e.target.value) })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </Field>
            </div>
            <Field label="Režim">
              <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                <option value="individual">Individuální</option>
                <option value="pairs">Páry</option>
                <option value="teams">Týmy</option>
                <option value="mixed">Smíšený</option>
              </select>
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.projector_enabled} onChange={(e) => setForm({ ...form, projector_enabled: e.target.checked })} />
              Zapnout projektor mode
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.safety_mode}
                onChange={(e) => setForm({ ...form, safety_mode: e.target.checked })}
                data-testid="toggle-safety-mode"
                className="mt-0.5"
              />
              <span>
                <span className="font-semibold">Bezpečný režim</span>
                <span className="block text-xs text-slate-500">Skryje citlivá ocenění (varování, „roast") ve veřejném ceremoniálu. Lektor je stále vidí v náhledu.</span>
              </span>
            </label>
            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 font-bold">
                Zrušit
              </button>
              <button onClick={createSession} className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-md px-4 py-2 text-sm" data-testid="btn-create-session-submit">
                Vytvořit session
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function SessionCard({ s, onOpen }) {
  const statusMap = {
    lobby: { label: "Čeká", color: "bg-amber-100 text-amber-800" },
    active: { label: "Aktivní", color: "bg-green-100 text-green-800" },
    paused: { label: "Pozastaveno", color: "bg-slate-100 text-slate-700" },
    finished: { label: "Ukončeno", color: "bg-slate-100 text-slate-500" },
  };
  const st = statusMap[s.status] || statusMap.lobby;
  return (
    <button
      onClick={onOpen}
      className="bg-white border border-slate-200 rounded-lg p-4 text-left hover:border-slate-900 hover:shadow-md transition-all"
      data-testid={`session-card-${s.code}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono font-bold text-lg text-slate-900">{s.code}</div>
        <div className="flex items-center gap-1">
          {s.safety_mode && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200" title="Safety mode">🛡️</span>
          )}
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
        </div>
      </div>
      <div className="font-bold text-slate-900 truncate">{s.title}</div>
      <div className="text-xs text-slate-500 mt-1">{s.workshop_title}</div>
      <div className="text-xs text-slate-400 mt-2">👥 {s.participant_count} účastníků</div>
    </button>
  );
}
